import { useEffect, useRef, useState } from 'react';
import { COVERAGE_LIVE } from '@tsr/ui';
import { openPopoutWindow } from './popout';
import { loadYouTubeIframeApi, type YTPlayer } from './youtubeIframe';
import { useElementSize } from './lib/useElementSize';

/**
 * What the YouTube embed actually permits for chrome suppression (#232 — the
 * documented iframe limits, verified against the IFrame API param docs):
 *   • controls=0     — removes the control bar (play/scrub/volume/settings).
 *   • disablekb=1    — disables keyboard transport on the focused player.
 *   • fs=0           — removes the fullscreen affordance.
 *   • iv_load_policy=3 — hides annotations/cards.
 *   • rel=0          — end/pause suggestions restricted to the same channel.
 *   • modestbranding — DEPRECATED by YouTube (no effect since June 2023); the
 *     control-bar logo is gone anyway with controls=0.
 * What CANNOT be removed by any parameter: the title/channel overlay that
 * flashes at load and reappears on pause, the "Watch on YouTube" logo while
 * paused, and the buffering spinner. Per YouTube ToS those must not be covered
 * by overlays while visible, so the remaining treatment is CONTAINER-level:
 * the wall's fill mode centre-crops the oversized 16:9 iframe to the tile
 * (broadcast-mosaic style), which keeps the tile reading as a composed feed
 * rather than an embedded webpage.
 */
const CHROMELESS_PLAYER_VARS = {
  autoplay: 1,
  mute: 1,
  controls: 0,
  disablekb: 1,
  fs: 0,
  iv_load_policy: 3,
  playsinline: 1,
  rel: 0,
} as const;
const DEFAULT_PLAYER_VARS = { autoplay: 1, mute: 1, controls: 1, rel: 0 } as const;

/** The 16:9 box that COVERS a w×h tile (centre-crop, broadcast-mosaic style).
 *  Pure so the cover math is unit-testable; {0,0} (unmeasured) → full-size. */
export function coverBoxFor(width: number, height: number): { width: string; height: string } {
  if (width <= 0 || height <= 0) return { width: '100%', height: '100%' };
  const VIDEO_AR = 16 / 9;
  if (width / height > VIDEO_AR) {
    return { width: `${width}px`, height: `${Math.ceil(width / VIDEO_AR)}px` };
  }
  return { width: `${Math.ceil(height * VIDEO_AR)}px`, height: `${height}px` };
}

/** Codes the IFrame API reports for an unplayable/embedding-blocked video. */
const FATAL_ERROR_CODES = new Set([100, 101, 150]);
/** If the stream hasn't reached PLAYING/BUFFERING within this window, treat the
 *  current unstarted/paused/cued state as "not live". */
const NOT_LIVE_TIMEOUT_MS = 5000;
/** Poll cadence for the currentTime-advance check below. */
const PROGRESS_POLL_MS = 2000;
/** Consecutive polls with no currentTime advance, after the player has reported
 *  PLAYING/BUFFERING, before we declare "no picture". The player is then
 *  claiming playback over a stalled / black frame (the YouTube analogue of
 *  LiveCameraPlayer's videoWidth===0 case) — we show an honest state instead of
 *  a LIVE badge. 4 × 2s = 8s rides out a normal mid-stream rebuffer. */
const NO_VIDEO_STALL_POLLS = 4;
/** A live currentTime tick smaller than this counts as "not advancing" (guards
 *  float jitter). */
const PROGRESS_EPSILON_S = 0.2;

type State = 'loading' | 'playing' | 'no_video' | 'not_live' | 'unavailable';

interface YoutubeResolveResponse {
  videoId: string | null;
  kind?: string;
  needsRediscovery?: boolean;
}

/**
 * YouTube-live player (Phase Q.1 §2.1) for stream_type='youtube_live' cameras.
 *
 * Resolves the camera to a current videoId via /api/cameras/:id/youtube (direct
 * rows are instant; channel-live rows resolve their /live page server-side,
 * 1 h cached), then mounts a YT.Player. Surfaces "stream not live" when the
 * player can't start within 5 s and "stream unavailable" on a fatal embed error
 * (100/101/150) or an unresolvable channel — the latter flags the row for
 * re-discovery.
 */
export function YouTubeLivePlayer({
  cameraId,
  name,
  enablePopout = true,
  resolveUrl,
  fill = false,
  chromeless = false,
  onPlaybackState,
}: {
  cameraId: string;
  name: string;
  enablePopout?: boolean;
  /** Endpoint that resolves this feed's current live videoId. Defaults to the
   *  camera path; the news tile (#182) points it at /api/news/:id/youtube so the
   *  exact same liveness chrome is reused for an ambient news outlet. */
  resolveUrl?: string;
  /** Wall fill mode (#232): the player fills its parent edge-to-edge (no card
   *  border / radius / fixed aspect) and the iframe is oversized to the 16:9
   *  box that COVERS the tile, centre-cropped — uniform mosaic, no black bars.
   *  Default false = the panel card, unchanged. */
  fill?: boolean;
  /** Suppress as much embed chrome as the IFrame API permits (see
   *  CHROMELESS_PLAYER_VARS above for exactly what it can and cannot hide) and
   *  drop our own interactive overlays (mute toggle, retry buttons) — the wall
   *  is read-only. */
  chromeless?: boolean;
  /** Reports the player's honest runtime up to a host caption (#232): the wall
   *  tile only captions LIVE while frames are confirmed flowing. */
  onPlaybackState?: (state: 'loading' | 'playing' | 'no_video' | 'offline') => void;
}): JSX.Element {
  const hostRef = useRef<HTMLDivElement | null>(null);
  // Tile measurement for fill mode's cover-crop (unused but harmless otherwise).
  const [frameRef, frameSize] = useElementSize<HTMLDivElement>();
  const playerRef = useRef<YTPlayer | null>(null);
  const stateRef = useRef<number>(-1);
  const [state, setState] = useState<State>('loading');
  const [muted, setMuted] = useState(true);
  const [attempt, setAttempt] = useState(0);

  // Honest runtime → host caption (#232): not_live / unavailable both read as
  // offline to the wall tile — the caption mirrors what the body is showing.
  useEffect(() => {
    if (!onPlaybackState) return;
    onPlaybackState(state === 'not_live' || state === 'unavailable' ? 'offline' : state);
  }, [state, onPlaybackState]);

  useEffect(() => {
    let destroyed = false;
    let notLiveTimer: ReturnType<typeof setTimeout> | undefined;
    let progressTimer: ReturnType<typeof setInterval> | undefined;
    setState('loading');

    const teardown = (): void => {
      if (notLiveTimer) clearTimeout(notLiveTimer);
      if (progressTimer) clearInterval(progressTimer);
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch {
          /* player already gone */
        }
        playerRef.current = null;
      }
    };

    void (async () => {
      // 1) Resolve the current videoId (direct = instant, channel = network).
      let videoId: string | null = null;
      try {
        const res = await fetch(resolveUrl ?? `/api/cameras/${cameraId}/youtube`);
        if (res.ok) {
          const body = (await res.json()) as YoutubeResolveResponse;
          videoId = body.videoId;
        }
      } catch {
        videoId = null;
      }
      if (destroyed) return;
      if (!videoId) {
        setState('unavailable');
        return;
      }

      // 2) Load the IFrame API and instantiate the player.
      let YT;
      try {
        YT = await loadYouTubeIframeApi();
      } catch {
        if (!destroyed) setState('unavailable');
        return;
      }
      if (destroyed || !hostRef.current) return;

      // A child div is created so YT.Player replaces it with the <iframe>,
      // leaving hostRef stable for our chrome (badge/popout/mute overlays).
      const mount = document.createElement('div');
      mount.className = 'h-full w-full';
      hostRef.current.appendChild(mount);

      // currentTime-advance monitor: the LIVE badge is only shown once real
      // frames are confirmed flowing (currentTime advances). A player that
      // claims PLAYING/BUFFERING but whose clock stays frozen is stalled over a
      // black/blank frame (Eagle Crest) — the cross-origin-iframe analogue of
      // LiveCameraPlayer's videoWidth===0 case, which we can't read here. We
      // surface an honest "no picture" state instead of badging LIVE over black.
      let lastTime = -1; // last observed currentTime (s)
      let stalledPolls = 0; // consecutive polls with no advance
      const startProgressMonitor = (): void => {
        if (progressTimer) return;
        progressTimer = setInterval(() => {
          if (destroyed) return;
          const p = playerRef.current;
          if (!p || typeof p.getCurrentTime !== 'function') {
            // Older/odd embeds without getCurrentTime: fall back to the
            // optimistic badge so we never regress a working feed.
            setState((s) => (s === 'loading' || s === 'no_video' ? 'playing' : s));
            return;
          }
          let t: number;
          try {
            t = p.getCurrentTime();
          } catch {
            return;
          }
          if (lastTime >= 0 && t > lastTime + PROGRESS_EPSILON_S) {
            // Real frames are decoding — confirmed live.
            stalledPolls = 0;
            setState('playing');
          } else {
            // Clock didn't advance this tick. After enough consecutive frozen
            // polls, the player is claiming playback over a stalled/black frame.
            stalledPolls += 1;
            if (stalledPolls >= NO_VIDEO_STALL_POLLS) {
              setState((s) => (s === 'playing' ? s : 'no_video'));
            }
          }
          if (t > lastTime) lastTime = t;
        }, PROGRESS_POLL_MS);
      };

      playerRef.current = new YT.Player(mount, {
        videoId,
        playerVars: { ...(chromeless ? CHROMELESS_PLAYER_VARS : DEFAULT_PLAYER_VARS) },
        events: {
          onReady: (e) => {
            // Honour the current mute toggle (defaults muted for autoplay).
            if (muted) e.target.mute();
            else e.target.unMute();
          },
          onStateChange: (e) => {
            stateRef.current = e.data;
            if (e.data === YT.PlayerState.PLAYING || e.data === YT.PlayerState.BUFFERING) {
              if (notLiveTimer) clearTimeout(notLiveTimer);
              // Don't badge LIVE yet — wait for currentTime to actually advance
              // (see the monitor above). This is what stops a LIVE-over-black.
              startProgressMonitor();
            }
          },
          onError: (e) => {
            if (!destroyed && FATAL_ERROR_CODES.has(e.data)) setState('unavailable');
          },
        },
      });

      // 3) 5 s watchdog — if still unstarted/paused/cued, it isn't live.
      notLiveTimer = setTimeout(() => {
        if (destroyed) return;
        const s = stateRef.current;
        const YTState = YT.PlayerState;
        if (s === YTState.UNSTARTED || s === YTState.PAUSED || s === YTState.CUED) {
          setState('not_live');
        }
      }, NOT_LIVE_TIMEOUT_MS);
    })();

    return () => {
      destroyed = true;
      teardown();
    };
    // `muted` intentionally excluded — the mute toggle drives the player
    // imperatively below so changing it doesn't tear down / re-resolve.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, attempt, resolveUrl, chromeless]);

  const toggleMute = (): void => {
    const p = playerRef.current;
    setMuted((m) => {
      const next = !m;
      if (p) {
        if (next) p.mute();
        else p.unMute();
      }
      return next;
    });
  };

  return (
    <div
      ref={fill ? frameRef : undefined}
      className={
        fill
          ? 'relative h-full w-full overflow-hidden bg-black'
          : 'relative w-full overflow-hidden rounded border border-slate-700 bg-black'
      }
      style={fill ? undefined : { aspectRatio: '16 / 9' }}
      data-testid="youtube-player"
      data-fill={fill ? 'true' : undefined}
    >
      {/* Fill mode: the host is the 16:9 box that COVERS the tile, centred —
          the iframe inside it is genuinely oversized, so the video centre-crops
          with no black bars and no squeeze (broadcast-mosaic treatment). */}
      <div
        ref={hostRef}
        className={fill ? 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2' : 'h-full w-full'}
        style={fill ? coverBoxFor(frameSize.width, frameSize.height) : undefined}
      />

      {enablePopout && (
        <button
          type="button"
          data-testid="popout-button"
          onClick={() => openPopoutWindow(cameraId)}
          title="Open in new window"
          aria-label="Open in new window"
          className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded bg-black/60 text-slate-200 hover:bg-black/80"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 4h6v6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20 4l-8 8" strokeLinecap="round" strokeLinejoin="round" />
            <path
              d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {state === 'playing' && (
        <>
          {/* Chromeless (wall) drops our own overlays too: the tile caption owns
              the LIVE marker there, and a read-only wall offers no mute. */}
          {!chromeless && (
            <div
              data-testid="live-badge"
              className="pointer-events-none absolute left-2 top-2 flex items-center gap-1.5 rounded bg-black/60 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider"
              style={{ color: COVERAGE_LIVE }}
            >
              <span
                className="inline-block h-2 w-2 animate-pulse rounded-full"
                style={{ backgroundColor: COVERAGE_LIVE }}
              />
              LIVE
            </div>
          )}
          {!chromeless && (
            <button
              type="button"
              data-testid="mute-toggle"
              onClick={toggleMute}
              title={muted ? 'Unmute' : 'Mute'}
              aria-label={muted ? 'Unmute' : 'Mute'}
              className="absolute bottom-2 right-2 z-10 flex h-7 items-center gap-1 rounded bg-black/60 px-2 text-[11px] font-medium text-slate-200 hover:bg-black/80"
            >
              {muted ? 'Muted' : 'Sound on'}
            </button>
          )}
        </>
      )}

      {state === 'loading' && (
        <div
          data-testid="youtube-loading"
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-300"
        >
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-500 border-t-transparent" />
          <span className="text-xs">Connecting to YouTube live…</span>
        </div>
      )}

      {state === 'not_live' && (
        <div
          data-testid="youtube-not-live"
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-950/80 text-center"
        >
          <span className="text-sm font-medium text-slate-200">Stream not live right now</span>
          <span className="px-6 text-xs text-slate-400">{name} is a YouTube live source that isn’t broadcasting at the moment.</span>
          {!chromeless && (
            <button
              type="button"
              onClick={() => setAttempt((a) => a + 1)}
              className="rounded border border-slate-600 bg-slate-800 px-3 py-1 text-xs text-slate-100 hover:bg-slate-700"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {state === 'no_video' && (
        // Honest state: YouTube reports the broadcast as playing, but the stream
        // isn't sending an advancing picture (stalled / black frame) — NOT a LIVE
        // badge over black. Recovers to 'playing' automatically if frames resume.
        <div
          data-testid="youtube-no-video"
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-950/80 text-center"
        >
          <span className="text-sm font-medium text-slate-200">No picture right now</span>
          <span className="px-6 text-xs text-slate-400">
            {name} is broadcasting but isn’t sending a moving picture at the moment
            (the camera may be down or dark).
          </span>
          {!chromeless && (
            <button
              type="button"
              onClick={() => setAttempt((a) => a + 1)}
              className="rounded border border-slate-600 bg-slate-800 px-3 py-1 text-xs text-slate-100 hover:bg-slate-700"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {state === 'unavailable' && (
        <div
          data-testid="youtube-unavailable"
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-950/80 text-center"
        >
          <span className="text-sm font-medium text-amber-300">Stream unavailable</span>
          <span className="px-6 text-xs text-slate-400">
            This YouTube source couldn’t be loaded and is flagged for re-discovery.
          </span>
          {!chromeless && (
            <button
              type="button"
              onClick={() => setAttempt((a) => a + 1)}
              className="rounded border border-slate-600 bg-slate-800 px-3 py-1 text-xs text-slate-100 hover:bg-slate-700"
            >
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}
